import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { isAllowedOfficeFileForKind, type DocumentEditorKind } from '../documentEditor/documentFileTypes';
import { isDrawioFile } from '../drawioEditor/drawioTypes';
import {
  openDocumentEditorSession,
  openEditorBySurface,
  openPdfViewerSession
} from '../editorShell/editorOpenHelpers';
import type { NewOfficeFileKind } from '../fileBrowser/fileBrowserNewFile';
import { isPdfFile } from '../pdfViewer/pdfFileTypes';
import type { StorageFileOpenPickerSelection } from '../fileBrowser/StorageFileOpenPickerPopup';
import { createBlankOfficeOnWritableMount } from './createBlankOfficeOnWritableMount';
import { createBlankStarterOnWritableMount } from './createBlankStarterOnWritableMount';
import { MyAppsFileLaunchPage } from './MyAppsFileLaunchPage';

const OFFICE_APPS = {
  richedit: {
    title: 'Document Editor',
    description:
      'Open a Word document from storage, or start a blank one, and edit it in Files → Workspace.',
    pickerTitle: 'Open document',
    newFileLabel: 'New blank document',
    newFileKind: 'docx' as NewOfficeFileKind
  },
  spreadsheet: {
    title: 'Spreadsheet Editor',
    description:
      'Open a spreadsheet from storage, or start a blank one, and edit it in Files → Workspace.',
    pickerTitle: 'Open spreadsheet',
    newFileLabel: 'New blank spreadsheet',
    newFileKind: 'xlsx' as NewOfficeFileKind
  }
} satisfies Record<DocumentEditorKind, unknown>;

export interface MyAppsOfficeLaunchPageProps {
  kind: DocumentEditorKind;
}

/** My Apps tile for the Office editors. The editor routes need a file, so pick one first. */
export function MyAppsOfficeLaunchPage({ kind }: MyAppsOfficeLaunchPageProps) {
  const navigate = useNavigate();
  const app = OFFICE_APPS[kind];

  const accept = useCallback(
    (fileName: string) => isAllowedOfficeFileForKind(fileName, kind),
    [kind]
  );

  const onOpen = useCallback(
    (selection: StorageFileOpenPickerSelection) => {
      openDocumentEditorSession({
        kind,
        mountId: selection.mountId,
        path: selection.path,
        accessMode: selection.mode === 'ReadWrite' ? 'edit' : 'view',
        preferredMode: 'inAppTab',
        navigate: (to) => navigate(to, { replace: true })
      });
    },
    [kind, navigate]
  );

  return (
    <MyAppsFileLaunchPage
      title={app.title}
      description={app.description}
      pickerTitle={app.pickerTitle}
      accept={accept}
      onOpen={onOpen}
      newFile={{
        label: app.newFileLabel,
        create: async (writableMounts) => {
          const created = await createBlankOfficeOnWritableMount(app.newFileKind, writableMounts);
          openDocumentEditorSession({
            kind: created.editorKind,
            mountId: created.mountId,
            path: created.relativePath,
            navigate: (to) => navigate(to, { replace: true })
          });
        }
      }}
    />
  );
}

export function MyAppsDocumentEditorPage() {
  return <MyAppsOfficeLaunchPage kind="richedit" />;
}

export function MyAppsSpreadsheetEditorPage() {
  return <MyAppsOfficeLaunchPage kind="spreadsheet" />;
}

/** PDF has no blank-file equivalent, so this app is picker-only. */
export function MyAppsPdfViewerPage() {
  const navigate = useNavigate();

  const onOpen = useCallback(
    (selection: StorageFileOpenPickerSelection) => {
      openPdfViewerSession({
        mountId: selection.mountId,
        path: selection.path,
        accessMode: selection.mode === 'ReadWrite' ? 'edit' : 'view',
        preferredMode: 'inAppTab',
        navigate: (to) => navigate(to, { replace: true })
      });
    },
    [navigate]
  );

  return (
    <MyAppsFileLaunchPage
      title="PDF Viewer"
      description="Pick a PDF from storage and read, search, or annotate it in Files → Workspace."
      pickerTitle="Open PDF"
      accept={isPdfFile}
      onOpen={onOpen}
    />
  );
}

/** Operations Hub tile. Draw.io already opens in Files → Workspace on that site. */
export function MyAppsDrawioEditorPage() {
  const navigate = useNavigate();

  const onOpen = useCallback((selection: StorageFileOpenPickerSelection) => {
    openEditorBySurface({
      surface: 'drawio',
      mountId: selection.mountId,
      path: selection.path,
      accessMode: selection.mode === 'ReadWrite' ? 'edit' : 'view',
      preferredMode: 'inAppTab',
      navigate: (to) => navigate(to, { replace: true })
    });
  }, [navigate]);

  return (
    <MyAppsFileLaunchPage
      title="Draw.io Editor"
      description="Open a diagram from storage, or start a blank one, and edit it in Files → Workspace."
      pickerTitle="Open diagram"
      accept={isDrawioFile}
      onOpen={onOpen}
      newFile={{
        label: 'New blank diagram',
        create: async (writableMounts) => {
          const created = await createBlankStarterOnWritableMount('drawio', writableMounts);
          openEditorBySurface({
            surface: 'drawio',
            mountId: created.mountId,
            path: created.relativePath,
            navigate: (to) => navigate(to, { replace: true })
          });
        }
      }}
    />
  );
}
